import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Check, AlertCircle } from "lucide-react";
import BtcLogo from "../components/BtcLogo";
import { api, useAuth } from "../lib/auth";

const KEY = "btc_pending_claim_token";

export default function Claim() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState("working"); // working | ok | error
  const [count, setCount] = useState(0);

  useEffect(() => {
    const token = (params.get("token") || sessionStorage.getItem(KEY) || "").trim();
    if (!token) return setStatus("error");
    if (user === null) return;
    if (user === false) {
      sessionStorage.setItem(KEY, token);
      navigate("/login", { replace: true, state: { from: { pathname: "/claim" } } });
      return;
    }
    sessionStorage.removeItem(KEY);
    api.post("/ops/my-applications/claim/confirm", { token })
      .then(({ data }) => { setCount(data.transferred_count || 0); setStatus("ok"); })
      .catch(() => setStatus("error"));
  }, [user, params, navigate]);

  return (
    <div className="min-h-screen bg-[#F8F9FB] flex items-center justify-center px-4">
      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm w-full max-w-md p-8 lg:p-10" data-testid="claim-page">
        <Link to="/" className="inline-flex items-center gap-2 mb-6"><BtcLogo className="h-12 w-auto" /></Link>
        <h1 className="text-3xl font-extrabold tracking-tight text-[#0B132B] mb-4">Claim your application</h1>
        {status === "working" && <p className="text-sm text-slate-600" data-testid="claim-working">Linking your application…</p>}
        {status === "ok" && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5" role="status" data-testid="claim-ok">
            <div className="flex items-start gap-3 text-sm text-emerald-900">
              <Check className="w-5 h-5 text-emerald-700 mt-0.5" />
              <div className="font-semibold">
                {count ? `${count} application${count === 1 ? "" : "s"} linked to your account.` : "Your application is linked to your account."}
              </div>
            </div>
            <Link to="/my-applications" className="mt-6 inline-flex btc-btn-primary" data-testid="claim-my-applications-link">
              View my applications
            </Link>
          </div>
        )}
        {status === "error" && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-5" role="alert" data-testid="claim-error">
            <div className="flex items-start gap-3 text-sm text-slate-700">
              <AlertCircle className="w-5 h-5 text-slate-500 mt-0.5" />
              <div>
                <div className="font-semibold text-slate-900 mb-1">This claim link is invalid or has expired.</div>
                <p>Sign in with the email address the link was sent to, or request a new link from My Applications.</p>
              </div>
            </div>
            <Link to="/my-applications" className="mt-5 inline-flex btc-btn-outline" data-testid="claim-error-my-applications-link">
              Go to My Applications
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
